import { CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Title, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { Youtube } from "react-bootstrap-icons";
import { Chart } from "react-chartjs-2";
import { useLoaderData } from "react-router-dom";
import ErrorResponse from "../ErrorResponse";
import { openYoutube } from "../HelperFunctions";
import { useFetchData } from "../HelperHooks";
import LoadingIndicator from "../LoadingIndicator";
import RATooltip from "../RATooltip";
import ScreenshotButton from "../ScreenshotButton";
import { backgroundPlugin } from "../chartOptions";

ChartJS.register(LinearScale, CategoryScale, PointElement, LineElement, Legend, Tooltip, ChartDataLabels, Title);

export default function HybridHitlistTrend({ show, onHide, hybridHitlistId, itemId }) {
    const { hitlists } = useLoaderData();

    const [loading, errorCode, data, fetchData] = useFetchData();

    const [grouping, setGrouping] = useState("month");
    const [monthsLimit, setMonthsLimit] = useState(8);
    const [hitlistName, setHitlistName] = useState("");

    const chartRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        async function loadData() {
            const url = `hybrid_hitlist/hybrid/${hybridHitlistId}/trend/${itemId}?months_limit=${monthsLimit}`;
            await fetchData(url);
        }
        if (hybridHitlistId && itemId && show) loadData();
    }, [hybridHitlistId, itemId, monthsLimit, show, fetchData]);

    useEffect(() => {
        if (data && data.data_week.length < 8) setGrouping("week");
    }, [data]);

    useEffect(() => {
        async function getHitlistName() {
            setHitlistName((await hitlists).data.find(h => h.hybrid_hitlist_id === hybridHitlistId)?.name || "");
        }
        if (hybridHitlistId) getHitlistName();
    }, [hybridHitlistId, hitlists]);

    const baseLineDataset = useMemo(
        () => ({
            type: "line",
            borderWidth: 4,
            pointBorderWidth: 4,
            pointHitRadius: 6,
            hoverRadius: 6,
            fill: false,
            datalabels: {
                align: "end",
                anchor: "end",
            },
        }),
        []
    );

    const chartOptions = useMemo(
        () => ({
            aspectRatio: window.innerHeight - 100 < window.innerWidth || window.innerWidth > 800 ? 2 : 1,
            borderRadius: 5,
            plugins: {
                title: {
                    display: true,
                    text:
                        `${data?.song?.artist} - ${data?.song?.title}`.length < 40
                            ? `${data?.song?.artist} - ${data?.song?.title}`
                            : [data?.song?.artist, data?.song?.title],
                    font: { size: 18 },
                    padding: { bottom: 5 },
                },
                subtitle: {
                    display: true,
                    text: `${hitlistName}${monthsLimit ? ` | Latest ${monthsLimit} months` : ""}`,
                    padding: { bottom: 24 },
                },
                datalabels: {
                    color: "#111",
                    textStrokeColor: "#fff",
                    textStrokeWidth: 3,
                    font: { size: 12, weight: "bold" },
                    display: ctx => {
                        const { chart, dataset, dataIndex } = ctx;
                        const value = dataset.data[dataIndex];

                        if (value == null) return false;

                        // Always show rank labels
                        if (dataset.label === "Rank") return true;

                        const datasetsAtIndex = chart.data.datasets.map(ds => ({ label: ds.label, value: ds.data[dataIndex] })).filter(d => d.value != null);

                        // Hide if overlapping another dataset
                        return !datasetsAtIndex.some(d => d.label !== dataset.label && Math.abs(d.value - value) < 5);
                    },
                },
                legend: {
                    position: "bottom",
                    labels: { usePointStyle: true },
                },
                customCanvasBackgroundColor: { color: "white" },
            },
            scales: {
                y: {
                    reverse: true,
                    grace: 0,
                    suggestedMin: 1,
                    suggestedMax: 100,
                    grid: { display: false },
                    ticks: { stepSize: 5 },
                },
                x: {
                    ticks: { maxRotation: 90 },
                    grid: { display: false },
                },
            },
            interaction: { mode: "index" },
        }),
        [data, hitlistName, monthsLimit]
    );

    const chartData = useMemo(() => {
        if (data) {
            const selectedGroupingData = grouping === "week" ? data.data_week : data.data_month;
            return {
                labels: selectedGroupingData.map(x => moment(x.basis_week).format(grouping === "week" ? "L" : "MMM YYYY")),
                datasets: [
                    {
                        ...baseLineDataset,
                        label: "Rank",
                        borderColor: "#3264B4",
                        pointBackgroundColor: "#3264B4",
                        data: selectedGroupingData.map(x => x.song_rank),
                    },
                    {
                        ...baseLineDataset,
                        label: "Airplay Rank",
                        borderColor: "#A52A2A",
                        pointBackgroundColor: "#A52A2A",
                        data: selectedGroupingData.map(x => x.airplay_rank),
                    },
                    {
                        ...baseLineDataset,
                        label: "Digital Rank",
                        borderColor: "#FEC834",
                        pointBackgroundColor: "#FEC834",
                        data: selectedGroupingData.map(x => x.digital_rank),
                    },
                ],
            };
        }
    }, [baseLineDataset, data, grouping]);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="trend-over-time-modal">
            <Modal.Header closeButton className=" py-1 py-xl-3">
                <Modal.Title className=" text-uppercase">Trend Over Time</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column pt-1 row-gap-2">
                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                    <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Group By:</span>
                    <ToggleButtonGroup type="radio" name="tot-grouping" className="ra-switcher" onChange={val => setGrouping(val)} value={grouping}>
                        <ToggleButton id={`tot-week`} value={"week"} variant="ra-dark-blue-outline" className=" align-content-center">
                            Week
                        </ToggleButton>
                        <ToggleButton id={`tot-month`} value={"month"} variant="ra-dark-blue-outline" className="d-flex align-items-center column-gap-1">
                            Month
                            <RATooltip size={18}>Monthly data includes all weeks starting in named month.</RATooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Limit:</span>
                    <ToggleButtonGroup type="radio" name="music-test-trend-limit" className="ra-switcher" onChange={setMonthsLimit} value={monthsLimit}>
                        <ToggleButton id={`music-test-trend-limit-0`} value={0} variant="ra-dark-blue-outline" className=" align-content-center">
                            Full
                        </ToggleButton>
                        <ToggleButton id={`music-test-trend-limit-8`} value={8} variant="ra-dark-blue-outline" className=" align-content-center text-nowrap">
                            8 Months
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant={"contained"}
                        onClick={() => openYoutube(data?.song)}
                        disabled={!data}
                        className="p-0 border-0 btn-download ms-auto d-flex align-items-center column-gap-2">
                        <Youtube width={35} height={35} />
                        <span className="d-none d-md-inline-block">Youtube</span>
                    </Button>
                    <ScreenshotButton
                        container={containerRef}
                        fileName={`Trend_${hitlistName}_${data?.song?.artist}_${data?.song?.title}`.replaceAll(" ", "_")}
                    />
                </Stack>
                <div ref={containerRef}>
                    {loading ? (
                        <LoadingIndicator width={75} />
                    ) : errorCode ? (
                        <ErrorResponse errorCode={errorCode} />
                    ) : (
                        data && <Chart ref={chartRef} data={chartData} options={chartOptions} plugins={[backgroundPlugin]} />
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export function useHybridHitlistTrendModal() {
    const [trendSong, setTrendSong] = useState(null);

    const HybridHitlistTrendModalComponent = ({ hybridHitlistId }) => (
        <HybridHitlistTrend show={!!trendSong} onHide={() => setTrendSong(null)} hybridHitlistId={hybridHitlistId} itemId={trendSong?.item_id} />
    );

    return { setTrendSong, HybridHitlistTrendModalComponent };
}
